import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faCheck, faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";
import { utilsService, DniTypeObject, applyDniPrefix, getMaxLengthFromMask, getDniCompositeKey, findDniTypeByCompositeKey } from "../../services/utils.service";
import AddressAutocomplete, { AddressComponents } from "./AddressAutocomplete";
import styles from "../../styles/common/EditAddressModal.module.css"; // Reuse the edit address modal styles
import modalStyles from "../../styles/common/Modal.module.css";
import { formatPhoneNational, isValidPhone, getPhonePlaceholder } from "../../config/phone.utils";
import { normalizeZip, isValidZipFormat, getZipFormatErrorMessage } from "../../config/zip.utils";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

interface EditRawAddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedData: any) => Promise<void>;
  initialData: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    kindOfDNI?: string;
    dni?: string;
  };
  title: string;
  showPersonalInfo?: boolean;
  phoneCode?: string;
}

const EditRawAddressModal: React.FC<EditRawAddressModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  title,
  showPersonalInfo,
  phoneCode,
}) => {
  const [values, setValues] = useState(initialData);
  const [errors, setErrors] = useState<any>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isValidatingZip, setIsValidatingZip] = useState(false);
  const [zipValidated, setZipValidated] = useState(true);
  const [zipCodeApiError, setZipCodeApiError] = useState<string | null>(null);
  const [dniTypes, setDniTypes] = useState<DniTypeObject[]>([]);

  useEffect(() => {
    const fetchDniTypes = async () => {
      if (showPersonalInfo && values.country) {
        try {
          const types = await utilsService.getDniTypes(values.country);
          setDniTypes(types);
        } catch (error) {
          console.error("Error fetching DNI types in modal:", error);
        }
      }
    };
    fetchDniTypes();
  }, [showPersonalInfo, values.country]);

  useEffect(() => {
    if (isOpen) {
      const formattedPhone = initialData.phone && initialData.country
        ? formatPhoneNational(initialData.phone, initialData.country, phoneCode)
        : initialData.phone || "";
      setValues({
        ...initialData,
        phone: formattedPhone
      });
      setErrors({});
      setZipValidated(true);
      setZipCodeApiError(null);
    }
  }, [isOpen, initialData, phoneCode]);

  const handleChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    let finalValue = value;
    if (name === "phone" && values.country) {
      finalValue = formatPhoneNational(value, values.country, phoneCode);
    }
    setValues((prev) => ({ ...prev, [name]: finalValue }));
    if (errors[name]) {
      setErrors((prev: any) => ({ ...prev, [name]: null }));
    }
  };

  const handleDniChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const selectedDniObj = findDniTypeByCompositeKey(dniTypes, values.kindOfDNI);
    const formatted = applyDniPrefix(val, selectedDniObj?.mask);
    setValues((prev) => ({
      ...prev,
      dni: formatted,
    }));

    // Validate on the fly
    let error: string | null = null;
    if (!formatted) {
      error = "Documento requerido";
    } else if (selectedDniObj && selectedDniObj.mask) {
      const regex = new RegExp(selectedDniObj.mask);
      if (!regex.test(formatted)) {
        error = `El documento no coincide con el formato requerido para ${values.kindOfDNI}.`;
      }
    }
    setErrors((prev: any) => ({
      ...prev,
      dni: error,
    }));
  };

  const handleKindOfDniChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setValues((prev) => ({
      ...prev,
      kindOfDNI: val,
      dni: "",
    }));
    setErrors((prev: any) => ({
      ...prev,
      kindOfDNI: null,
      dni: null,
    }));
  };

  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setValues((prev) => ({ ...prev, zipCode: value }));
    setZipValidated(false);
    setZipCodeApiError(null);
    if (errors.zipCode) {
      setErrors((prev: any) => ({ ...prev, zipCode: null }));
    }
  };

  const handleAddressSelect = async (addressData: AddressComponents) => {
    const country = values.country || "US";
    const normalizedZip = normalizeZip(addressData.zipCode || "", country);
    const newValues = {
      ...values,
      addressLine1: addressData.fullAddress,
      city: addressData.city || values.city || "",
      state: addressData.state || values.state || "",
      zipCode: normalizedZip || values.zipCode || "",
    };
    
    setValues(newValues);
    setErrors((prev: any) => ({
      ...prev,
      addressLine1: null,
      city: null,
      state: null,
      zipCode: null,
    }));
    setZipCodeApiError(null);
    setZipValidated(true);
  };

  const validateModalZip = async (zipToValidate?: string): Promise<{ city: string; state: string; zipCode: string } | null> => {
    const rawZip = zipToValidate !== undefined ? zipToValidate : values.zipCode;
    if (!rawZip) return null;
    const country = values.country || "US";
    const zip = normalizeZip(rawZip, country);

    if (!isValidZipFormat(zip, country)) {
      setZipCodeApiError(getZipFormatErrorMessage(country));
      setZipValidated(false);
      return null;
    }

    setZipCodeApiError(null);
    setZipValidated(true);
    setIsValidatingZip(true);

    try {
      const location = await utilsService.getLocationByZipcode({
        iso2code: country,
        zipcode: zip,
      });

      const newCity = location?.city || values.city;
      const newState = location?.state || values.state;
      const updatedFields = {
        city: newCity,
        state: newState,
        zipCode: zip,
      };
      setErrors((prevErrors: any) => ({
        ...prevErrors,
        city: newCity ? undefined : prevErrors.city,
        state: newState ? undefined : prevErrors.state,
      }));
      setValues((prev) => ({
        ...prev,
        ...updatedFields,
      }));
      return updatedFields;
    } catch (error) {
      console.warn("Non-blocking zip lookup notice:", error);
      const updatedFields = {
        city: values.city,
        state: values.state,
        zipCode: zip,
      };
      return updatedFields;
    } finally {
      setIsValidatingZip(false);
    }
  };

  const validate = () => {
    const newErrors: any = {};
    if (showPersonalInfo) {
      if (!values.firstName) newErrors.firstName = "El nombre es requerido";
      if (!values.lastName) newErrors.lastName = "El apellido es requerido";
      if (!values.email) {
        newErrors.email = "El email es requerido";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
        newErrors.email = "Email inválido";
      }
      if (!values.phone) {
        newErrors.phone = "El teléfono es requerido";
      } else if (!isValidPhone(values.phone, values.country)) {
        newErrors.phone = "Número de teléfono inválido";
      }
      if (!values.kindOfDNI) newErrors.kindOfDNI = "Tipo de doc. requerido";
      if (!values.dni) {
        newErrors.dni = "Documento requerido";
      } else {
        const selectedDniObj = findDniTypeByCompositeKey(dniTypes, values.kindOfDNI);
        if (selectedDniObj && selectedDniObj.mask) {
          const regex = new RegExp(selectedDniObj.mask);
          if (!regex.test(values.dni)) {
            newErrors.dni = `El documento no coincide con el formato requerido para ${selectedDniObj.kindOfDNI}.`;
          }
        }
      }
    }
    if (!values.addressLine1) newErrors.addressLine1 = "La dirección es requerida";
    if (!values.city) newErrors.city = "La ciudad es requerida";
    if (!values.state) newErrors.state = "El estado es requerido";
    if (!values.zipCode) newErrors.zipCode = "El código postal es requerido";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    // Sync browser autofill values
    let currentValues = { ...values };
    const modalElement = document.querySelector('.modal.show');
    if (modalElement) {
      const inputs = modalElement.querySelectorAll('input, select, textarea');
      let hasChanges = false;
      inputs.forEach((input: any) => {
        const name = input.getAttribute('name');
        if (name && input.value !== undefined && input.value !== (currentValues as any)[name]) {
          if (input.type === 'checkbox') {
            (currentValues as any)[name] = input.checked;
          } else {
            (currentValues as any)[name] = input.value;
          }
          hasChanges = true;
        }
      });
      if (hasChanges) {
        if (currentValues.phone && currentValues.country) {
          currentValues.phone = formatPhoneNational(currentValues.phone, currentValues.country, phoneCode);
        }
        if (currentValues.dni) {
          const selectedDniObj = findDniTypeByCompositeKey(dniTypes, currentValues.kindOfDNI);
          currentValues.dni = applyDniPrefix(currentValues.dni, selectedDniObj?.mask);
        }
        setValues(currentValues);
      }
    }

    // Validation using synced values
    const newErrors: any = {};
    if (showPersonalInfo) {
      if (!currentValues.firstName) newErrors.firstName = "El nombre es requerido";
      if (!currentValues.lastName) newErrors.lastName = "El apellido es requerido";
      if (!currentValues.email) {
        newErrors.email = "El email es requerido";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentValues.email)) {
        newErrors.email = "Email inválido";
      }
      if (!currentValues.phone) {
        newErrors.phone = "El teléfono es requerido";
      } else if (!isValidPhone(currentValues.phone, currentValues.country)) {
        newErrors.phone = "Número de teléfono inválido";
      }
      if (!currentValues.kindOfDNI) newErrors.kindOfDNI = "Tipo de doc. requerido";
      if (!currentValues.dni) {
        newErrors.dni = "Documento requerido";
      } else {
        const selectedDniObj = findDniTypeByCompositeKey(dniTypes, currentValues.kindOfDNI);
        if (selectedDniObj && selectedDniObj.mask) {
          const regex = new RegExp(selectedDniObj.mask);
          if (!regex.test(currentValues.dni)) {
            newErrors.dni = `El documento no coincide con el formato requerido para ${selectedDniObj.kindOfDNI}.`;
          }
        }
      }
    }
    if (!currentValues.addressLine1) newErrors.addressLine1 = "La dirección es requerida";
    if (!currentValues.city) newErrors.city = "La ciudad es requerida";
    if (!currentValues.state) newErrors.state = "El estado es requerido";
    if (!currentValues.zipCode) newErrors.zipCode = "El código postal es requerido";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    if (!zipValidated) {
      const updatedZipData = await validateModalZip();
      if (!updatedZipData) {
        return;
      }
      currentValues = {
        ...currentValues,
        ...updatedZipData,
      };
    }
    setIsSaving(true);
    try {
      await onSave(currentValues);
      onClose();
    } catch (error) {
      console.error("Error saving address:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      centered
      size="lg"
      className={styles.editAddressModal}
      contentClassName={styles.scaleIn}
      animation={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className={`${styles.modalTitle} ${modalStyles.modalTitle}`}>
          <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" /> {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form
          noValidate
          onInput={(e: React.FormEvent<HTMLFormElement>) => {
            const target = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
            const name = target.name;
            const value = target.value;
            if (name) {
              let val = value;
              if (name === "phone" && values.country) {
                val = formatPhoneNational(value, values.country, phoneCode);
              } else if (name === "dni") {
                const selectedDniObj = findDniTypeByCompositeKey(dniTypes, values.kindOfDNI);
                val = applyDniPrefix(value, selectedDniObj?.mask);
              }
              setValues((prev) => {
                if ((prev as any)[name] === val) return prev;
                return {
                  ...prev,
                  [name]: val
                };
              });
            }
          }}
        >
          <Row className="g-3">
            {showPersonalInfo && (
              <>
                <Col md={6}>
                  <Form.Group controlId="firstName">
                    <Form.Label style={{ fontWeight: 600, color: "#475569" }}>Nombre</Form.Label>
                    <Form.Control
                      type="text"
                      name="firstName"
                      value={values.firstName || ""}
                      onChange={handleChange}
                      isInvalid={!!errors.firstName}
                      className={styles.editAddressModalInput}
                      maxLength={50}
                    />
                    <Form.Control.Feedback type="invalid">{errors.firstName}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="lastName">
                    <Form.Label style={{ fontWeight: 600, color: "#475569" }}>Apellido</Form.Label>
                    <Form.Control
                      type="text"
                      name="lastName"
                      value={values.lastName || ""}
                      onChange={handleChange}
                      isInvalid={!!errors.lastName}
                      className={styles.editAddressModalInput}
                      maxLength={50}
                    />
                    <Form.Control.Feedback type="invalid">{errors.lastName}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="email">
                    <Form.Label style={{ fontWeight: 600, color: "#475569" }}>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={values.email || ""}
                      onChange={handleChange}
                      isInvalid={!!errors.email}
                      className={styles.editAddressModalInput}
                      maxLength={100}
                    />
                    <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="phone">
                    <Form.Label style={{ fontWeight: 600, color: "#475569" }}>Teléfono</Form.Label>
                    <div
                      className={`${styles.phoneInputWrapper} ${
                        errors.phone ? "is-invalid" : ""
                      }`}
                    >
                      {values.country && (
                        <div className={styles.phonePrefix}>
                          <img
                            src={`https://flagcdn.com/w40/${values.country.toLowerCase()}.png`}
                            srcSet={`https://flagcdn.com/w80/${values.country.toLowerCase()}.png 2x`}
                            alt={values.country}
                            className={styles.phonePrefixFlag}
                            loading="lazy"
                            decoding="async"
                          />
                          {phoneCode && (
                            <span className={styles.phonePrefixCode}>{phoneCode}</span>
                          )}
                        </div>
                      )}
                      <input
                        type="text"
                        name="phone"
                        value={values.phone || ""}
                        onChange={handleChange}
                        placeholder={getPhonePlaceholder(values.country || "US")}
                        maxLength={15}
                      />
                    </div>
                    {errors.phone && (
                      <div className="invalid-feedback d-block">{errors.phone}</div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="kindOfDNI">
                    <Form.Label style={{ fontWeight: 600, color: "#475569" }}>Tipo Doc.</Form.Label>
                    <Form.Select
                      name="kindOfDNI"
                      value={values.kindOfDNI || ""}
                      onChange={handleKindOfDniChange}
                      isInvalid={!!errors.kindOfDNI}
                      className={styles.editAddressModalInput}
                    >
                      <option value="">Seleccione...</option>
                      {dniTypes.map((t) => (
                        <option key={getDniCompositeKey(t)} value={getDniCompositeKey(t)}>
                          {t.prefix || t.kindOfDNI}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">{errors.kindOfDNI}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="dni">
                    <Form.Label style={{ fontWeight: 600, color: "#475569" }}>Documento</Form.Label>
                    <Form.Control
                      type="text"
                      name="dni"
                      value={values.dni || ""}
                      onChange={handleDniChange}
                      isInvalid={!!errors.dni}
                      className={styles.editAddressModalInput}
                      maxLength={getMaxLengthFromMask(findDniTypeByCompositeKey(dniTypes, values.kindOfDNI)?.mask) || 20}
                    />
                    <Form.Control.Feedback type="invalid">{errors.dni}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </>
            )}
            <Col md={12}>
              <Form.Group controlId="addressLine1">
                <Form.Label style={{ fontWeight: 600, color: "#475569" }}>Dirección</Form.Label>
                {GOOGLE_API_KEY ? (
                  <AddressAutocomplete
                    value={values.addressLine1}
                    onChange={(value) => setValues((prev) => ({
                      ...prev,
                      addressLine1: value,
                      city: "",
                      state: "",
                      zipCode: ""
                    }))}
                    onAddressSelect={handleAddressSelect}
                    placeholder="Calle, Av, Edificio..."
                    inputName="addressLine1"
                    apiKey={GOOGLE_API_KEY}
                    inputClassName={`${styles.editAddressModalInput} ${
                      errors.addressLine1 ? "is-invalid" : ""
                    }`}
                    country={values.country.toLowerCase()}
                  />
                ) : (
                  <Form.Control
                    type="text"
                    name="addressLine1"
                    value={values.addressLine1}
                    onChange={handleChange}
                    isInvalid={!!errors.addressLine1}
                    className={styles.editAddressModalInput}
                    maxLength={150}
                  />
                )}
                {errors.addressLine1 && (
                  <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                    {errors.addressLine1}
                  </div>
                )}
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group controlId="addressLine2">
                <Form.Label style={{ fontWeight: 600, color: "#475569" }}>Línea 2 (Opcional)</Form.Label>
                <Form.Control
                  type="text"
                  name="addressLine2"
                  value={values.addressLine2 || ""}
                  onChange={handleChange}
                  className={styles.editAddressModalInput}
                  maxLength={150}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group controlId="city">
                <Form.Label style={{ fontWeight: 600, color: "#475569" }}>Ciudad</Form.Label>
                <Form.Control
                  type="text"
                  name="city"
                  value={values.city}
                  onChange={handleChange}
                  isInvalid={!!errors.city}
                  className={styles.editAddressModalInput}
                  maxLength={50}
                />
                <Form.Control.Feedback type="invalid">{errors.city}</Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group controlId="state">
                <Form.Label style={{ fontWeight: 600, color: "#475569" }}>Estado</Form.Label>
                <Form.Control
                  type="text"
                  name="state"
                  value={values.state}
                  onChange={handleChange}
                  isInvalid={!!errors.state}
                  className={styles.editAddressModalInput}
                  maxLength={50}
                />
                <Form.Control.Feedback type="invalid">{errors.state}</Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group controlId="zipCode">
                <Form.Label style={{ fontWeight: 600, color: "#475569" }}>Código Postal</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    name="zipCode"
                    value={values.zipCode}
                    onChange={handleZipChange}
                    onBlur={() => validateModalZip(values.zipCode)}
                    isInvalid={!!errors.zipCode || !!zipCodeApiError}
                    className={styles.editAddressModalInput}
                    maxLength={10}
                  />
                  {isValidatingZip && (
                    <div className="position-absolute end-0 top-50 translate-middle-y pe-3">
                      <Spinner animation="border" size="sm" />
                    </div>
                  )}
                  <Form.Control.Feedback type="invalid">
                    {errors.zipCode || zipCodeApiError}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <button className={styles.secondaryBtn} onClick={onClose} disabled={isSaving}>
          Cancelar
        </button>
        <Button className={modalStyles.closeBtn} onClick={handleSave} disabled={isSaving || !zipValidated}>
          {isSaving ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin className="me-2" /> Guardando...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faCheck} className="me-2" /> Guardar
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditRawAddressModal;
